import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft, Play, RotateCcw, Check, Trophy, Clock, Target, Zap, Coins,
  GraduationCap, Dumbbell, Timer, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BottomNav } from "@/components/student/BottomNav";
import { SorobanAbacus, stateToValue } from "@/components/student/SorobanAbacus";

const MODES = [
  { id: "learn", name: "Learn", desc: "Free play & guided bead practice", icon: GraduationCap, timed: false },
  { id: "practice", name: "Practice", desc: "Untimed scored questions", icon: Dumbbell, timed: false },
  { id: "timed", name: "Timed", desc: "Beat the clock for bonus XP", icon: Timer, timed: true },
];
const LEVELS = ["auto", "beginner", "easy", "medium", "hard", "expert"];

export default function AbacusPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [exercises, setExercises] = useState([]);
  const [phase, setPhase] = useState("home"); // home|learn|countdown|play|result
  const [mode, setMode] = useState("practice");
  // Deep link from teacher-assigned practice: /abacus?exercise=addition&level=easy
  const urlParams = new URLSearchParams(window.location.search);
  const [exercise, setExercise] = useState(urlParams.get("exercise") || "addition");
  const [difficulty, setDifficulty] = useState(["beginner", "easy", "medium", "hard", "expert"].includes(urlParams.get("level")) ? urlParams.get("level") : "auto");

  const [session, setSession] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [value, setValue] = useState(0);
  const [typed, setTyped] = useState("");
  const [countdown, setCountdown] = useState(3);
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);

  // learn-mode target
  const [learnTarget, setLearnTarget] = useState(3456);
  const [learnValue, setLearnValue] = useState(0);

  const answersRef = useRef([]);
  const shownAtRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/abacus/exercises`, { headers }).then((r) => setExercises(r.data.exercises || [])).catch(() => {});
    return () => clearInterval(timerRef.current);
  }, []);

  const start = async () => {
    if (mode === "learn") {
      setLearnTarget(randomTarget());
      setLearnValue(0);
      setPhase("learn");
      return;
    }
    try {
      const r = await axios.post(`${API}/abacus/start`, {
        exercise, difficulty, count: 10, timed: mode === "timed",
      }, { headers });
      setSession(r.data);
      answersRef.current = [];
      setQIndex(0);
      setValue(0);
      setTyped("");
      setCountdown(3);
      setPhase("countdown");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start");
    }
  };

  // countdown
  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown <= 0) { beginQuestion(0); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const beginQuestion = (idx) => {
    setQIndex(idx);
    setValue(0);
    setTyped("");
    shownAtRef.current = Date.now();
    setPhase("play");
    clearInterval(timerRef.current);
    if (session?.timed && session?.time_per_question) {
      setTimeLeft(session.time_per_question);
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { clearInterval(timerRef.current); submitAnswer(true); return 0; }
          return t - 1;
        });
      }, 1000);
    }
  };

  const submitAnswer = (timeout = false) => {
    clearInterval(timerRef.current);
    const q = session.questions[qIndex];
    const ans = timeout ? "" : (typed !== "" ? typed : String(value));
    answersRef.current = [...answersRef.current, {
      question_id: q.id, answer: String(ans), response_ms: Date.now() - shownAtRef.current,
    }];
    if (qIndex + 1 < session.questions.length) {
      beginQuestion(qIndex + 1);
    } else {
      finish();
    }
  };

  const finish = async () => {
    try {
      const r = await axios.post(`${API}/abacus/submit`, {
        session_id: session.session_id, answers: answersRef.current,
      }, { headers });
      setResult(r.data);
      setPhase("result");
    } catch (e) {
      toast.error("Couldn't submit run");
      setPhase("home");
    }
  };

  // ---------- RENDER ----------
  if (phase === "home") {
    return (
      <Shell navigate={navigate}>
        <h1 className="text-3xl font-extrabold text-slate-800 font-['Fredoka'] mb-1">Abacus Trainer</h1>
        <p className="text-slate-500 mb-6 font-['Lexend']">Soroban mental maths — Learn, Practice & race the clock.</p>

        <Label>Mode</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)} data-testid={`abacus-mode-${m.id}`}
              className={`text-left rounded-2xl p-4 border-2 transition-all ${mode === m.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <m.icon className={`w-6 h-6 mb-2 ${mode === m.id ? "text-indigo-600" : "text-slate-400"}`} />
              <p className="font-bold text-slate-800">{m.name}</p>
              <p className="text-xs text-slate-500">{m.desc}</p>
            </button>
          ))}
        </div>

        {mode !== "learn" && (
          <>
            <Label>Exercise</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              {exercises.map((e) => (
                <button key={e.id} onClick={() => setExercise(e.id)} data-testid={`abacus-exercise-${e.id}`}
                  className={`rounded-xl px-3 py-2.5 text-sm font-semibold border-2 transition-all ${exercise === e.id ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                  {e.name}
                </button>
              ))}
            </div>

            <Label>Difficulty</Label>
            <div className="flex flex-wrap gap-2 mb-8">
              {LEVELS.map((l) => (
                <button key={l} onClick={() => setDifficulty(l)} data-testid={`abacus-difficulty-${l}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize border-2 transition-all ${difficulty === l ? "border-amber-500 bg-amber-100 text-amber-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                  {l}
                </button>
              ))}
            </div>
          </>
        )}

        <Button onClick={start} data-testid="abacus-start-btn" className="w-full h-14 rounded-2xl text-lg font-bold bg-indigo-600 hover:bg-indigo-700">
          <Play className="w-5 h-5 mr-2" /> {mode === "learn" ? "Open Abacus" : "Start"}
        </Button>
      </Shell>
    );
  }

  if (phase === "learn") {
    return (
      <Shell navigate={navigate} onBack={() => setPhase("home")}>
        <h1 className="text-2xl font-extrabold text-slate-800 font-['Fredoka'] mb-2">Learn the Abacus</h1>
        <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 mb-4">
          <p className="text-sm text-slate-500 mb-1">Set this number on the abacus:</p>
          <p className="text-4xl font-extrabold text-indigo-600 font-['Fredoka']" data-testid="learn-target">{learnTarget.toLocaleString()}</p>
        </div>
        <div className="flex justify-center mb-4 overflow-x-auto">
          <SorobanAbacus rods={5} value={learnValue} onChange={setLearnValue} />
        </div>
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-slate-500">Your abacus shows</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="learn-value">{learnValue.toLocaleString()}</p>
          </div>
          {learnValue === learnTarget ? (
            <span className="inline-flex items-center gap-1 text-green-600 font-bold" data-testid="learn-correct"><Check className="w-5 h-5" /> Perfect!</span>
          ) : (
            <span className="text-slate-400 text-sm">Keep going…</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setLearnValue(0)} data-testid="learn-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
          <Button className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700" onClick={() => { setLearnTarget(randomTarget()); setLearnValue(0); }} data-testid="learn-next">New number <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
        <BottomNav />
      </Shell>
    );
  }

  if (phase === "countdown") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-indigo-50">
        <div key={countdown} className="text-8xl font-extrabold text-indigo-600 font-['Fredoka'] animate-bounce" data-testid="abacus-countdown">
          {countdown > 0 ? countdown : "Go!"}
        </div>
      </div>
    );
  }

  if (phase === "play" && session) {
    const q = session.questions[qIndex];
    return (
      <div className="min-h-screen bg-slate-50 pb-6">
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <span className="text-sm font-semibold text-slate-500" data-testid="abacus-q-counter">Q {qIndex + 1}/{session.questions.length}</span>
          {session.timed ? (
            <span className={`text-sm font-bold ${timeLeft <= 5 ? "text-red-500" : "text-slate-600"}`} data-testid="abacus-timer"><Clock className="w-4 h-4 inline mr-1" />{timeLeft}s</span>
          ) : <span className="text-xs text-slate-400 capitalize">{session.exercise}</span>}
        </div>
        <div className="max-w-xl mx-auto px-4 pt-5">
          <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 mb-4 text-center">
            {q.rows ? (
              <div className="inline-block text-right font-mono text-2xl font-bold text-slate-800 leading-8" data-testid="abacus-question">
                {q.rows.map((r, i) => (
                  <div key={i}>{i === 0 ? r : (String(r).startsWith("-") ? `− ${r.slice(1)}` : `+ ${r}`)}</div>
                ))}
                <div className="border-t-2 border-slate-800 mt-1 pt-1">= ?</div>
              </div>
            ) : (
              <p className="text-3xl font-extrabold text-slate-800 font-['Fredoka']" data-testid="abacus-question">{q.prompt} = ?</p>
            )}
          </div>

          <div className="flex justify-center mb-3 overflow-x-auto">
            <SorobanAbacus rods={7} value={value} onChange={(v) => { setValue(v); setTyped(String(v)); }} />
          </div>

          <div className="flex items-center gap-2 mb-4">
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitAnswer(false); }}
              inputMode="decimal"
              placeholder="Answer (abacus or type)"
              className="h-12 text-lg text-center font-bold"
              data-testid="abacus-answer-input"
            />
            <Button onClick={() => submitAnswer(false)} data-testid="abacus-submit-btn" className="h-12 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 font-bold">
              {qIndex + 1 === session.questions.length ? "Finish" : "Next"}
            </Button>
          </div>
          <p className="text-center text-xs text-slate-400">Form the answer on the abacus, or type it. Abacus value auto-fills.</p>
        </div>
      </div>
    );
  }

  if (phase === "result" && result) {
    return (
      <Shell navigate={navigate} onBack={() => setPhase("home")}>
        <div className="text-center mb-6">
          <Trophy className="w-14 h-14 text-amber-400 mx-auto mb-2" />
          <p className="text-slate-500 font-['Lexend']">Run complete!</p>
          <p className="text-5xl font-extrabold text-indigo-600 font-['Fredoka']" data-testid="abacus-result-score">{result.score}</p>
          <p className="text-xs text-slate-400 uppercase tracking-widest">score</p>
          {result.personal_best && <span className="inline-block mt-2 text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-3 py-1">🏅 New personal best!</span>}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Stat icon={Target} label="Accuracy" value={`${result.accuracy}%`} />
          <Stat icon={Check} label="Correct" value={`${result.correct}/${result.total}`} />
          <Stat icon={Zap} label="Best Combo" value={result.longest_combo} />
          <Stat icon={Clock} label="Avg Time" value={`${(result.avg_response_ms/1000).toFixed(1)}s`} />
          <Stat icon={Zap} label="XP" value={`+${result.xp}`} />
          <Stat icon={Coins} label="Coins" value={`+${result.coins}`} />
        </div>
        <p className="text-center text-sm text-slate-500 mb-4">Next suggested level: <span className="font-bold capitalize text-indigo-600">{result.next_difficulty}</span></p>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setPhase("home")} data-testid="abacus-home-btn">Change</Button>
          <Button className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700" onClick={() => { setDifficulty(result.next_difficulty); start(); }} data-testid="abacus-again-btn">Play again</Button>
        </div>
        <BottomNav />
      </Shell>
    );
  }

  return null;
}

function Shell({ children, navigate, onBack }) {
  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-white border-b px-4 py-3">
        <button onClick={() => (onBack ? onBack() : navigate("/student"))} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-semibold" data-testid="abacus-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-6">{children}</div>
      <BottomNav />
    </div>
  );
}

function Label({ children }) {
  return <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{children}</p>;
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-3 text-center">
      <Icon className="w-5 h-5 text-indigo-500 mx-auto mb-1" />
      <p className="text-lg font-extrabold text-slate-800 font-['Fredoka'] leading-none">{value}</p>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

function randomTarget() {
  return Math.floor(Math.random() * 90000) + 100;
}
